import { useCallback, useEffect, useState } from "react";
import { Brain, Check, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, getStaffToken } from "@/lib/api";

const cfg = () => ({ headers: { Authorization: `Bearer ${getStaffToken()}` } });

// Fixes Apex learned from staff chats, waiting for a one-tap approve/delete.
export function SuggestedFixes({ onApproved }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/staff/kb-suggestions`, cfg()).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => {
    try {
      await axios.post(`${API}/staff/kb-suggestions/${id}/approve`, {}, cfg());
      toast.success("Added to the Knowledge Base");
      onApproved && onApproved();
    } catch { toast.error("Couldn't approve that suggestion"); }
    load();
  };
  const remove = async (id) => {
    try { await axios.delete(`${API}/staff/kb-suggestions/${id}`, cfg()); } catch { toast.error("Couldn't delete that suggestion"); }
    load();
  };
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-[var(--primary)]/50 bg-[var(--primary)]/5 p-3 space-y-2" data-testid="kb-suggestions">
      <div className="flex items-center gap-2"><Sparkles size={14} className="text-[var(--primary)]" />
        <span className="text-sm font-semibold">Suggested from chats ({items.length})</span></div>
      {items.map((s) => (
        <div key={s.id} className="rounded-lg bg-black/30 border border-[var(--border)] p-2.5" data-testid={`kb-suggestion-${s.id}`}>
          <p className="text-sm font-semibold text-[var(--text-primary)]">{s.issue}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">{s.fix}</p>
          <div className="flex gap-2 mt-2">
            <button data-testid={`kb-suggestion-approve-${s.id}`} onClick={() => approve(s.id)}
              className="flex items-center gap-1 rounded-md bg-[var(--primary)] text-black text-xs font-semibold px-2.5 py-1 hover:bg-[var(--primary-hover)] transition-colors">
              <Check size={13} /> Approve
            </button>
            <button data-testid={`kb-suggestion-delete-${s.id}`} onClick={() => remove(s.id)}
              className="flex items-center gap-1 rounded-md border border-[var(--border)] text-[var(--text-secondary)] text-xs px-2.5 py-1 hover:border-red-500 hover:text-red-400 transition-colors">
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Everything Apex remembers (customer facts + staff preferences), grouped by subject.
export function MemoryList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    axios.get(`${API}/staff/memory`, cfg()).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    try { await axios.delete(`${API}/staff/memory/${id}`, cfg()); toast.success("Forgotten"); } catch { toast.error("Couldn't forget that"); }
    load();
  };
  const groups = items.reduce((acc, m) => { (acc[m.subject] = acc[m.subject] || []).push(m); return acc; }, {});
  return (
    <div className="p-4 space-y-3" data-testid="memory-list">
      <div className="flex items-start gap-2 text-xs text-[var(--text-muted)]">
        <Brain size={14} className="text-[var(--tech-accent)] shrink-0 mt-0.5" />
        Apex learns these from staff chats (about 10 minutes after a chat goes quiet, plus nightly). Customers never see them.
        Ask "what do you remember about Smith?" or say "forget that".
      </div>
      {loading && <p className="text-sm text-[var(--text-muted)]" data-testid="memory-loading">Loading memories…</p>}
      {!loading && !items.length && <p className="text-sm text-[var(--text-secondary)]">Nothing remembered yet.</p>}
      {Object.entries(groups).map(([subject, list]) => (
        <div key={subject} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3">
          <p className="text-sm font-semibold text-[var(--tech-accent)] mb-1.5">
            {subject} <span className="text-[10px] font-mono-tech uppercase text-[var(--text-muted)]">{list[0].kind}</span>
          </p>
          <ul className="space-y-1.5">
            {list.map((m) => (
              <li key={m.id} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]" data-testid={`memory-item-${m.id}`}>
                <span className="flex-1">{m.fact}</span>
                <button data-testid={`memory-delete-${m.id}`} onClick={() => remove(m.id)} title="Forget"
                  className="text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0"><Trash2 size={13} /></button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
